import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/ai-revenue-agents", label: "AI Agents" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="sozos-container flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group shrink-0">
          <img
            src="/brand/sozos-logo.png"
            alt="Sozos"
            className="h-9 md:h-10 w-auto"
          />
          <span className="hidden md:inline text-[10px] uppercase tracking-[0.22em] text-slate-500 pl-2 border-l border-slate-200 leading-tight">
            Global<br />Services
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-[#1E40AF]" : "text-slate-700 hover:text-[#0A1F44]"
                }`
              }
              end={n.to === "/"}
            >
              <span className="link-underline">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex">
          <button
            data-testid="nav-cta-book"
            onClick={() => navigate("/contact#book")}
            className="btn-primary"
          >
            Book a Growth Consultation <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden p-2 text-[#0A1F44]"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200">
          <div className="sozos-container py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm font-medium border-b border-slate-100 ${
                    isActive ? "text-[#1E40AF]" : "text-slate-700"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <button
              data-testid="mobile-cta-book"
              onClick={() => {
                setOpen(false);
                navigate("/contact#book");
              }}
              className="btn-primary mt-4 w-full"
            >
              Book a Growth Consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
